/**
 * Authentication Zustand store.
 *
 * Manages user authentication state with:
 * - User info storage
 * - Authentication status tracking
 * - Login/logout actions
 * - Session persistence via localStorage token
 */

import { create } from 'zustand';

import { authApi, type User } from '@/api/auth';
import { ApiError } from '@/api/errors';

/**
 * Auth status values.
 */
export type AuthStatus =
  | 'idle'
  | 'loading'
  | 'restoring'
  | 'authenticated'
  | 'unauthenticated'
  | 'unreachable';

/**
 * localStorage key for session detection.
 * Note: The actual auth is cookie-based, this is just for quick session detection.
 */
const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_RETRY_BASE_DELAY_MS = 1_000;
const AUTH_RETRY_MAX_DELAY_MS = 15_000;
// Session restore must terminate: after the initial probe plus this many
// backoff retries, surface an explicit "server unreachable" state instead of
// spinning forever behind the bootstrap screen.
const AUTH_RESTORE_MAX_RETRY_ATTEMPTS = 3;
// Bounds a single /auth/me probe (including transport-level retries) so a
// hanging proxy cannot stall session restore indefinitely.
const AUTH_CHECK_TIMEOUT_MS = 10_000;

let authRetryTimeout: ReturnType<typeof setTimeout> | null = null;
let authRetryAttempt = 0;
let inflightCheckAuth: Promise<void> | null = null;
let inflightCheckRunId = 0;
let authEpoch = 0;
let authRecoveryRetryRequired = false;

function hasStoredSessionHint(): boolean {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY) === 'session';
  } catch {
    return false;
  }
}

function writeStoredSessionHint(): void {
  try {
    localStorage.setItem(AUTH_TOKEN_KEY, 'session');
  } catch {
    // Ignore storage failures; the server cookie remains authoritative.
  }
}

function clearStoredSessionHint(): void {
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    // Ignore storage failures; the server cookie remains authoritative.
  }
}

function clearScheduledAuthRetry(options?: { resetAttempts?: boolean }): void {
  const { resetAttempts = true } = options ?? {};
  if (authRetryTimeout !== null) {
    clearTimeout(authRetryTimeout);
    authRetryTimeout = null;
  }
  if (resetAttempts) {
    authRetryAttempt = 0;
  }
}

function scheduleAuthRetry(retry: () => Promise<void>): void {
  if (authRetryTimeout !== null) {
    return;
  }

  const delayMs = Math.min(
    AUTH_RETRY_MAX_DELAY_MS,
    AUTH_RETRY_BASE_DELAY_MS * 2 ** authRetryAttempt,
  );
  authRetryAttempt += 1;
  authRetryTimeout = setTimeout(() => {
    authRetryTimeout = null;
    void retry();
  }, delayMs);
}

function beginAuthMutation(): number {
  authEpoch += 1;
  inflightCheckRunId += 1;
  inflightCheckAuth = null;
  authRecoveryRetryRequired = false;
  clearScheduledAuthRetry();
  return authEpoch;
}

function isStaleAuthEpoch(epoch: number): boolean {
  return epoch !== authEpoch;
}

export function __resetAuthRuntimeForTests(): void {
  authEpoch = 0;
  inflightCheckRunId = 0;
  inflightCheckAuth = null;
  authRecoveryRetryRequired = false;
  clearScheduledAuthRetry();
}

/**
 * Authentication store state and actions.
 */
export interface AuthState {
  /**
   * Current authenticated user, null if not authenticated.
   */
  user: User | null;

  /**
   * Current authentication status.
   */
  status: AuthStatus;

  /**
   * Login with username (email) and password.
   * Sets user and status on success, returns the authenticated user, throws ApiError on failure.
   * Returns null when a newer auth mutation superseded this login before it completed.
   */
  login: (username: string, password: string) => Promise<User | null>;

  /**
   * Logout current session.
   * Clears user and sets status to unauthenticated.
   */
  logout: () => Promise<void>;

  /**
   * Check current authentication status.
   * Fetches user info from /auth/me endpoint.
   * Sets status to authenticated or unauthenticated based on response.
   */
  checkAuth: (options?: { retryOnTransient?: boolean }) => Promise<void>;

  /**
   * Clear auth state without API call.
   * Used for handling 401 errors.
   */
  clearAuth: (options?: { preserveSessionHint?: boolean }) => void;
}

/**
 * Create the auth store with Zustand.
 */
export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: 'idle',

  login: async (username: string, password: string) => {
    const authMutationEpoch = beginAuthMutation();
    set({ status: 'loading' });

    try {
      const { user } = await authApi.login(username, password);
      if (isStaleAuthEpoch(authMutationEpoch)) {
        return null;
      }

      writeStoredSessionHint();

      set({ user, status: 'authenticated' });
      return user;
    } catch (error) {
      if (isStaleAuthEpoch(authMutationEpoch)) {
        return null;
      }
      clearStoredSessionHint();
      set({ user: null, status: 'unauthenticated' });
      throw error;
    }
  },

  logout: async () => {
    const authMutationEpoch = beginAuthMutation();
    set({ status: 'loading' });

    try {
      await authApi.logout();
    } catch (error) {
      // Log but don't block logout on API error
      console.error('Logout API error:', error);
    } finally {
      if (isStaleAuthEpoch(authMutationEpoch)) {
        return;
      }
      // Always clear local state
      clearStoredSessionHint();
      set({ user: null, status: 'unauthenticated' });
    }
  },

  checkAuth: async (options) => {
    // An observed auth boundary is stronger evidence than the best-effort
    // localStorage hint. Keep retry intent sticky across an already-inflight
    // probe and across storage-restricted browser environments.
    if (options?.retryOnTransient) {
      authRecoveryRetryRequired = true;
    }
    if (inflightCheckAuth) {
      return inflightCheckAuth;
    }

    const fallbackUser = useAuthStore.getState().user;
    const hasSessionHint = hasStoredSessionHint();
    // A manual retry from the unreachable screen starts a fresh backoff window.
    clearScheduledAuthRetry({ resetAttempts: useAuthStore.getState().status === 'unreachable' });

    if (!fallbackUser) {
      set({ status: hasSessionHint ? 'restoring' : 'loading' });
    }

    const checkEpoch = authEpoch;
    const checkRunId = inflightCheckRunId + 1;
    inflightCheckRunId = checkRunId;
    const promise = (async () => {
      try {
        const { user } = await authApi.me({ signal: AbortSignal.timeout(AUTH_CHECK_TIMEOUT_MS) });
        if (isStaleAuthEpoch(checkEpoch)) {
          return;
        }
        writeStoredSessionHint();
        authRecoveryRetryRequired = false;
        clearScheduledAuthRetry();
        set({ user, status: 'authenticated' });
      } catch (error) {
        if (isStaleAuthEpoch(checkEpoch)) {
          return;
        }

        if (error instanceof ApiError && error.isAuthError()) {
          clearStoredSessionHint();
          authRecoveryRetryRequired = false;
          clearScheduledAuthRetry();
          set({ user: null, status: 'unauthenticated' });
          return;
        }

        // For transient 5xx/network issues, keep the last known session state
        // and retry in the background instead of force-logging out on restart.
        if (fallbackUser) {
          set({ user: fallbackUser, status: 'authenticated' });
          scheduleAuthRetry(useAuthStore.getState().checkAuth);
        } else if (hasSessionHint || authRecoveryRetryRequired) {
          if (authRetryAttempt >= AUTH_RESTORE_MAX_RETRY_ATTEMPTS) {
            console.error(
              `[Auth] session restore failed after ${authRetryAttempt + 1} attempts; server unreachable`,
            );
            set({ user: null, status: 'unreachable' });
          } else {
            set({ user: null, status: 'restoring' });
            scheduleAuthRetry(useAuthStore.getState().checkAuth);
          }
        } else {
          set({ user: null, status: 'unauthenticated' });
        }

        if (error instanceof ApiError && !error.isAuthError()) {
          console.error('[Auth] checkAuth failed:', error.statusCode, error.message);
        } else if (!(error instanceof ApiError)) {
          console.error('[Auth] checkAuth failed:', error);
        }
      } finally {
        if (inflightCheckRunId === checkRunId) {
          inflightCheckAuth = null;
        }
      }
    })();

    inflightCheckAuth = promise;
    return promise;
  },

  clearAuth: (options) => {
    beginAuthMutation();
    if (!options?.preserveSessionHint) {
      clearStoredSessionHint();
    }
    set({ user: null, status: 'unauthenticated' });
  },
}));

/**
 * Selector for checking if user is authenticated.
 */
export const selectIsAuthenticated = (state: AuthState): boolean =>
  state.status === 'authenticated' && state.user !== null;
